import './style.css';
import { ColorGridShader } from './ColorGridShader.js';

// Initialize application
document.addEventListener('DOMContentLoaded', () => {
    const app = document.getElementById('app');
    const settingsToggleBtn = document.getElementById('settingsToggleBtn');
    const settingsCloseBtn = document.getElementById('settingsCloseBtn');
    const settingsBackdrop = document.getElementById('settingsBackdrop');
    const canvas = document.getElementById('mainCanvas');
    const shader = new ColorGridShader(canvas);

    const faviconLink = document.getElementById('favicon');
    let lastFaviconHex = null;
    const setFaviconColor = (hex) => {
        if (!faviconLink) return;
        if (typeof hex !== 'string' || !hex) return;
        if (lastFaviconHex === hex) return;
        lastFaviconHex = hex;

        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><rect width="100" height="100" fill="${hex}"/></svg>`;
        faviconLink.setAttribute('href', `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`);
    };
    
    const dropZone = document.getElementById('dropZone');
    const fileInput = document.getElementById('fileInput');
    const gridColsInput = document.getElementById('gridCols');
    const gridRowsInput = document.getElementById('gridRows');
    const gapSlider = document.getElementById('gapSlider');
    const cornerRadiusSlider = document.getElementById('cornerRadiusSlider');
    const sourceLightenSlider = document.getElementById('sourceLightenSlider');
    const sourceBwToggle = document.getElementById('sourceBwToggle');
    const colorCountSlider = document.getElementById('colorCountSlider');
    const extractionModeSelect = document.getElementById('extractionModeSelect');
    const thresholdSlider = document.getElementById('thresholdSlider');
    const copyJsonBtn = document.getElementById('copyJsonBtn');
    const downloadPngBtn = document.getElementById('downloadPngBtn');
    const colorList = document.getElementById('colorList');
    const tooltip = document.getElementById('tooltip');
    const showFpsToggle = document.getElementById('showFpsToggle');
    const fpsOverlay = document.getElementById('fpsOverlay');
    
    const colsValue = document.getElementById('colsValue');
    const rowsValue = document.getElementById('rowsValue');
    const gapValue = document.getElementById('gapValue');
    const cornerRadiusValue = document.getElementById('cornerRadiusValue');
    const sourceLightenValue = document.getElementById('sourceLightenValue');
    const colorCountValue = document.getElementById('colorCountValue');
    const thresholdValue = document.getElementById('thresholdValue');
    const revealValue = document.getElementById('revealValue');
    const revealSlider = document.getElementById('revealSlider');
    const revealDetailsToggle = document.getElementById('revealDetailsToggle');
    const invertRevealToggle = document.getElementById('invertRevealToggle');
    const webcamBtn = document.getElementById('webcamBtn');
    const switchCameraGroup = document.getElementById('switchCameraGroup');
    const switchCameraBtn = document.getElementById('switchCameraBtn');
    const pauseBtn = document.getElementById('pauseBtn');
    const triggerCaptureBtn = document.getElementById('triggerCaptureBtn');
    const effectSelect = document.getElementById('effectSelect');
    const hideSourceToggle = document.getElementById('hideSourceToggle');
    const groupRectModeToggle = document.getElementById('groupRectModeToggle');
    const resetAllBtn = document.getElementById('resetAllBtn');
    const changeImageBtn = document.getElementById('changeImageBtn');

    const targetColorsGroup = colorCountSlider?.closest?.('.control-group');

    const hideSourceGroup = hideSourceToggle?.closest?.('.control-group');
    const sourceLightenGroup = sourceLightenSlider?.closest?.('.control-group');
    const sourceBwGroup = sourceBwToggle?.closest?.('.control-group');
    const groupRectModeGroup = groupRectModeToggle?.closest?.('.control-group');
    
    const statCells = document.getElementById('statCells');
    const statColors = document.getElementById('statColors');
    const statWidth = document.getElementById('statWidth');
    const statHeight = document.getElementById('statHeight');

    if (app && settingsToggleBtn) {
        const syncAria = () => {
            const isOpen = app.classList.contains('settings-open');
            settingsToggleBtn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        };

        const isMobileLayout = () => {
            // Keep in sync with the CSS breakpoint where the canvas becomes a fixed preview.
            return typeof window !== 'undefined'
                && typeof window.matchMedia === 'function'
                && window.matchMedia('(max-width: 600px)').matches;
        };

        const closeSettings = () => {
            app.classList.remove('settings-open');
            syncAria();
            window.dispatchEvent(new Event('resize'));
        };

        settingsToggleBtn.addEventListener('click', () => {
            app.classList.toggle('settings-open');
            syncAria();
            window.dispatchEvent(new Event('resize'));
        });

        settingsCloseBtn?.addEventListener('click', closeSettings);
        settingsBackdrop?.addEventListener('click', closeSettings);

        // Mobile UX: when the settings panel is open, the canvas becomes a fixed-position preview.
        // Tapping that preview should close the settings panel.
        canvas?.addEventListener('click', (e) => {
            if (!isMobileLayout()) return;
            if (!app.classList.contains('settings-open')) return;
            e.preventDefault();
            closeSettings();
        });

        syncAria();
    }

    let resizeRaf = 0;
    const syncPreviewAspect = () => {
        if (!app || !canvas) return;
        const w = canvas.width || canvas.clientWidth;
        const h = canvas.height || canvas.clientHeight;
        if (!w || !h) return;
        app.style.setProperty('--preview-aspect', String(w / h));
    };

    window.addEventListener('resize', () => {
        if (resizeRaf) cancelAnimationFrame(resizeRaf);
        resizeRaf = requestAnimationFrame(() => {
            resizeRaf = 0;
            shader.resizeToContainer?.();
            syncPreviewAspect();
            scheduleUpdateUI();
        });
    });

    syncPreviewAspect();

    let webcamActive = false;
    let videoActive = false;
    let sourceLoaded = false;
    let settingsWereChanged = false;

    let showFpsEnabled = false;
    let fpsFrames = 0;
    let fpsWindowStart = 0;

    const resetFps = () => {
        fpsFrames = 0;
        fpsWindowStart = performance.now();
        if (fpsOverlay) fpsOverlay.textContent = 'FPS: --';
    };

    const setShowFpsEnabled = (enabled) => {
        showFpsEnabled = !!enabled;
        if (fpsOverlay) {
            fpsOverlay.classList.toggle('is-hidden', !showFpsEnabled);
            fpsOverlay.setAttribute('aria-hidden', showFpsEnabled ? 'false' : 'true');
        }
        resetFps();
    };

    const updateFps = () => {
        if (!showFpsEnabled || !fpsOverlay) return;
        const now = performance.now();
        if (!fpsWindowStart) fpsWindowStart = now;

        fpsFrames += 1;
        const elapsed = now - fpsWindowStart;
        if (elapsed < 400) return;

        const fps = (fpsFrames * 1000) / elapsed;
        fpsOverlay.textContent = `FPS: ${fps.toFixed(1)}`;
        fpsFrames = 0;
        fpsWindowStart = now;
    };

    const onShaderFrame = () => {
        updateFps();
        scheduleUpdateUI();
    };

    if (showFpsToggle) {
        showFpsToggle.addEventListener('change', (e) => {
            setShowFpsEnabled(e.target.checked);
        });
        setShowFpsEnabled(showFpsToggle.checked);
    } else {
        // If the control is absent for any reason, keep the overlay hidden.
        setShowFpsEnabled(false);
    }

    // UI updates (stats + extracted colors list + CSS custom props) are relatively expensive.
    // Throttle them so video/webcam doesn't rebuild DOM every frame.
    const UI_THROTTLE_MS = 120;
    let uiUpdateTimer = 0;
    let uiLastRunAt = 0;

    const DEFAULTS = {
        gridCols: 15,
        gridRows: 15,
        gap: 0,
        cornerRadius: 0,
        sourceLighten: 0,
        sourceBw: false,
        targetColors: 8,
        extractionMode: 'lab',
        groupThreshold: 20,
        revealSize: 20,
        revealDetails: false,
        invertReveal: false,
        effect: 'none',
        hideSource: false,
        groupRectMode: false,
    };

    const syncExtractionDependentControls = (mode) => {
        const normalized = (mode === 'rgb-kmeans' || mode === 'rgb' || mode === 'gifenc') ? mode : 'lab';
        const usesKMeans = normalized === 'lab' || normalized === 'rgb-kmeans' || normalized === 'gifenc';

        if (colorCountSlider) colorCountSlider.disabled = !usesKMeans;
        if (targetColorsGroup) {
            targetColorsGroup.classList.toggle('is-disabled', !usesKMeans);
        }
    };

    const applyEffectToShader = (effect) => {
        const normalized = (effect === 'gradient' || effect === 'line' || effect === 'dither') ? effect : 'none';
        shader.setGradientMode?.(normalized === 'gradient');
        shader.setLineMode?.(normalized === 'line');
        shader.setDitherMode?.(normalized === 'dither');
    };

    const updateActionButtons = () => {
        if (changeImageBtn) changeImageBtn.disabled = !sourceLoaded;
        if (resetAllBtn) resetAllBtn.disabled = !settingsWereChanged;
        if (triggerCaptureBtn) triggerCaptureBtn.disabled = !sourceLoaded || !videoActive;
    };

    const markSettingsChanged = () => {
        settingsWereChanged = true;
        updateActionButtons();
    };

    const syncRevealDependentControls = () => {
        if (!revealSlider) return;

        const revealSize = parseInt(revealSlider.value || '0', 10) || 0;
        const shouldEnable = revealSize !== 0;

        if (hideSourceToggle && hideSourceGroup) {
            hideSourceToggle.disabled = !shouldEnable;
            hideSourceGroup.classList.toggle('is-disabled', !shouldEnable);
        }

        if (sourceLightenSlider && sourceLightenGroup) {
            sourceLightenSlider.disabled = !shouldEnable;
            sourceLightenGroup.classList.toggle('is-disabled', !shouldEnable);
        }

        if (sourceBwToggle && sourceBwGroup) {
            sourceBwToggle.disabled = !shouldEnable;
            sourceBwGroup.classList.toggle('is-disabled', !shouldEnable);
        }

        // If the control is disabled, keep behavior consistent by turning it off.
        if (!shouldEnable) {
            if (hideSourceToggle?.checked) {
                hideSourceToggle.checked = false;
                shader.setHideSource?.(false);
            }

            if (sourceLightenSlider && (parseInt(sourceLightenSlider.value || '0', 10) || 0) !== 0) {
                sourceLightenSlider.value = '0';
                if (sourceLightenValue) sourceLightenValue.textContent = '0%';
                shader.setSourceLighten?.(0);
            }

            if (sourceBwToggle?.checked) {
                sourceBwToggle.checked = false;
                shader.setSourceGrayscale?.(false);
            }
        }
    };

    const syncGroupRectDependentControls = () => {
        if (!thresholdSlider || !groupRectModeToggle || !groupRectModeGroup) return;

        const threshold = parseInt(thresholdSlider.value || '0', 10) || 0;
        const shouldEnable = threshold !== 0;

        groupRectModeToggle.disabled = !shouldEnable;
        groupRectModeGroup.classList.toggle('is-disabled', !shouldEnable);

        if (!shouldEnable && groupRectModeToggle.checked) {
            groupRectModeToggle.checked = false;
            shader.setGroupRectMode?.(false);
        }
    };

    const scheduleUpdateUI = (opts = {}) => {
        const immediate = !!opts.immediate;

        if (immediate) {
            if (uiUpdateTimer) {
                clearTimeout(uiUpdateTimer);
                uiUpdateTimer = 0;
            }
            uiLastRunAt = performance.now();
            updateUI();
            return;
        }

        const now = performance.now();
        const elapsed = now - uiLastRunAt;
        const remaining = UI_THROTTLE_MS - elapsed;

        if (remaining <= 0) {
            uiLastRunAt = now;
            updateUI();
            return;
        }

        if (uiUpdateTimer) return;
        uiUpdateTimer = window.setTimeout(() => {
            uiUpdateTimer = 0;
            uiLastRunAt = performance.now();
            updateUI();
        }, remaining);
    };

    function setMediaActive({ webcam, video }) {
        webcamActive = !!webcam;
        videoActive = !!video;

        if (switchCameraGroup) {
            switchCameraGroup.classList.toggle('is-hidden', !videoActive);
        }
        if (pauseBtn) {
            pauseBtn.textContent = 'Pause';
            pauseBtn.dataset.paused = 'false';
        }
        // Switch camera visibility handled by updateSwitchCameraAvailability()
        if (switchCameraBtn) {
            switchCameraBtn.classList.add('is-hidden');
        }

        updateActionButtons();
    }

    async function updateSwitchCameraAvailability() {
        if (!switchCameraBtn || !shader?.getWebcamCameraCount) return;
        try {
            const count = await shader.getWebcamCameraCount();
            const canSwitch = webcamActive && videoActive && count >= 2;
            switchCameraBtn.disabled = !canSwitch;
            switchCameraBtn.classList.toggle('is-hidden', !canSwitch);
        } catch {
            switchCameraBtn.disabled = true;
            switchCameraBtn.classList.add('is-hidden');
        }
    }

    setMediaActive({ webcam: false, video: false });
    
    function updateUI() {
        const data = shader.getGridData();
        
        statCells.textContent = data.length;
        const colorCounts = new Map();
        const colorSampleCell = new Map();

        for (const cell of data) {
            const hex = cell.color.hex;
            colorCounts.set(hex, (colorCounts.get(hex) || 0) + 1);
            if (!colorSampleCell.has(hex)) {
                colorSampleCell.set(hex, cell);
            }
        }

        const colorsByCount = [...colorCounts.entries()]
            .map(([hex, count]) => ({ hex, count, cell: colorSampleCell.get(hex) }))
            .sort((a, b) => {
                if (b.count !== a.count) return b.count - a.count;
                return a.hex.localeCompare(b.hex);
            });

        // Expose key colors as CSS custom properties
        // --v1: most common (dominant)
        // --c0: least common
        const rootStyle = document.documentElement?.style;
        if (rootStyle) {
            if (colorsByCount.length > 0) {
                const mostCommonHex = colorsByCount[0].hex;
                const leastCommonHex = colorsByCount[colorsByCount.length - 1].hex;
                rootStyle.setProperty('--c1', mostCommonHex);
                rootStyle.setProperty('--c0', leastCommonHex);

                // Keep the favicon in sync with the footer logo accent color (--c1).
                setFaviconColor(mostCommonHex);
            } else {
                rootStyle.removeProperty('--c1');
                rootStyle.removeProperty('--c0');

                // Fallback to the default favicon color.
                setFaviconColor('#000');
            }
        }

        // Provide dominant color to shader (used when Hide Source is enabled)
        if (colorsByCount.length > 0) {
            const dominant = colorsByCount[0].cell?.color;
            if (dominant) {
                shader.setBackgroundColorRGB?.(dominant.r / 255, dominant.g / 255, dominant.b / 255);
            }
        }

        statColors.textContent = colorsByCount.length;
        statWidth.textContent = canvas.width;
        statHeight.textContent = canvas.height;
        
        // Show only unique colors
        colorList.innerHTML = '';
        colorsByCount.forEach(({ hex, count }) => {
            const item = document.createElement('div');
            item.className = 'color-item';
            item.innerHTML = `
                <div class="color-swatch" style="background-color: ${hex}"></div>
                <div class="color-info">
                    <div class="color-hex">${hex}</div>
                    <div class="color-position">${count} cell${count > 1 ? 's' : ''}</div>
                </div>
            `;
            item.addEventListener('click', () => {
                navigator.clipboard.writeText(hex);
                showTooltip(item, 'Copied!');
            });
            colorList.appendChild(item);
        });
    }
    
    function showTooltip(element, text) {
        const rect = element.getBoundingClientRect();
        tooltip.textContent = text;
        tooltip.style.left = rect.left + 'px';
        tooltip.style.top = (rect.top - 30) + 'px';
        tooltip.classList.add('visible');
        setTimeout(() => tooltip.classList.remove('visible'), 1000);
    }
    
    function loadFile(file) {
        setMediaActive({ webcam: false, video: false });
        const isVideo = file.type.startsWith('video/');
        
        if (isVideo) {
            const video = document.createElement('video');
            video.muted = true;
            video.loop = true;
            video.playsInline = true;
            video.crossOrigin = 'anonymous';
            
            const reader = new FileReader();
            reader.onload = (e) => {
                video.src = e.target.result;
                
                video.addEventListener('loadeddata', () => {
                    dropZone.classList.add('has-image');
                    video.play().catch(() => {});
                    shader.loadVideo(video);
                    shader.onFrameUpdate = onShaderFrame;
                    setMediaActive({ webcam: false, video: true });
                    sourceLoaded = true;
                    updateActionButtons();
                    scheduleUpdateUI({ immediate: true });
                });
                
                video.addEventListener('error', () => {
                    alert('Video loading failed. Try opening the file locally for better video support.');
                });
            };
            reader.readAsDataURL(file);
        } else {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    dropZone.classList.add('has-image');
                    shader.loadImage(img);
                    setMediaActive({ webcam: false, video: false });
                    sourceLoaded = true;
                    updateActionButtons();
                    scheduleUpdateUI({ immediate: true });
                };
                img.src = e.target.result;
            };
            reader.readAsDataURL(file);
        }
    }
    
    dropZone.addEventListener('click', () => fileInput.click());
    
    document.addEventListener('dragover', (e) => {
        e.preventDefault();
        dropZone.classList.add('dragover');
    });
    
    document.addEventListener('dragleave', (e) => {
        if (!e.relatedTarget || !document.contains(e.relatedTarget)) {
            dropZone.classList.remove('dragover');
        }
    });
    
    document.addEventListener('drop', (e) => {
        e.preventDefault();
        dropZone.classList.remove('dragover');
        if (e.dataTransfer.files.length) {
            loadFile(e.dataTransfer.files[0]);
        }
    });
    
    fileInput.addEventListener('change', (e) => {
        if (e.target.files.length) {
            loadFile(e.target.files[0]);
        }
    });

    const applyDefaultsToUI = () => {
        gridColsInput.value = String(DEFAULTS.gridCols);
        colsValue.textContent = String(DEFAULTS.gridCols);

        gridRowsInput.value = String(DEFAULTS.gridRows);
        rowsValue.textContent = String(DEFAULTS.gridRows);

        gapSlider.value = String(DEFAULTS.gap);
        gapValue.textContent = String(DEFAULTS.gap);

        if (cornerRadiusSlider) cornerRadiusSlider.value = String(DEFAULTS.cornerRadius);
        if (cornerRadiusValue) cornerRadiusValue.textContent = DEFAULTS.cornerRadius + '%';

        if (sourceLightenSlider) sourceLightenSlider.value = String(DEFAULTS.sourceLighten);
        if (sourceLightenValue) sourceLightenValue.textContent = DEFAULTS.sourceLighten + '%';

        if (sourceBwToggle) sourceBwToggle.checked = DEFAULTS.sourceBw;

        colorCountSlider.value = String(DEFAULTS.targetColors);
        colorCountValue.textContent = String(DEFAULTS.targetColors);

        if (extractionModeSelect) extractionModeSelect.value = DEFAULTS.extractionMode;

        syncExtractionDependentControls(DEFAULTS.extractionMode);

        thresholdSlider.value = String(DEFAULTS.groupThreshold);
        thresholdValue.textContent = DEFAULTS.groupThreshold + '%';

        revealSlider.value = String(DEFAULTS.revealSize);
        revealValue.textContent = DEFAULTS.revealSize + '%';

        if (revealDetailsToggle) revealDetailsToggle.checked = DEFAULTS.revealDetails;
        if (invertRevealToggle) invertRevealToggle.checked = DEFAULTS.invertReveal;
        if (effectSelect) effectSelect.value = DEFAULTS.effect;
        if (hideSourceToggle) hideSourceToggle.checked = DEFAULTS.hideSource;
        if (groupRectModeToggle) groupRectModeToggle.checked = DEFAULTS.groupRectMode;

        syncRevealDependentControls();
        syncGroupRectDependentControls();
    };

    const applyDefaultsToShader = () => {
        shader.setGridSize(DEFAULTS.gridCols, DEFAULTS.gridRows);
        shader.setGap(DEFAULTS.gap / 100);
        shader.setCornerRadius?.(DEFAULTS.cornerRadius / 100);
        shader.setSourceLighten?.(DEFAULTS.sourceLighten / 100);
        shader.setSourceGrayscale?.(DEFAULTS.sourceBw);
        shader.setTargetColors(DEFAULTS.targetColors);
        shader.setExtractionMode?.(DEFAULTS.extractionMode);
        shader.setGroupThreshold(DEFAULTS.groupThreshold);
        shader.setRevealSize(DEFAULTS.revealSize);
        shader.setRevealDetails?.(DEFAULTS.revealDetails);
        shader.setInvertReveal?.(DEFAULTS.invertReveal);
        applyEffectToShader(DEFAULTS.effect);
        shader.setHideSource?.(DEFAULTS.hideSource);
        shader.setGroupRectMode?.(DEFAULTS.groupRectMode);
    };

    if (resetAllBtn) {
        resetAllBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            applyDefaultsToUI();
            applyDefaultsToShader();

            // Keep "Reset all settings" consistent: default is FPS hidden.
            if (showFpsToggle) {
                showFpsToggle.checked = false;
            }
            setShowFpsEnabled(false);

            settingsWereChanged = false;
            updateActionButtons();
            scheduleUpdateUI({ immediate: true });
        });
    }

    if (changeImageBtn) {
        changeImageBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();

            shader.clearSource?.();
            shader.onFrameUpdate = null;

            dropZone.classList.remove('has-image');
            dropZone.classList.remove('dragover');
            fileInput.value = '';
            setMediaActive({ webcam: false, video: false });
            sourceLoaded = false;
            updateActionButtons();
            scheduleUpdateUI({ immediate: true });
        });
    }
    
    gridColsInput.addEventListener('input', () => {
        const val = parseInt(gridColsInput.value);
        colsValue.textContent = val;
        markSettingsChanged();
        shader.setGridSize(val, parseInt(gridRowsInput.value));
        scheduleUpdateUI();
    });
    
    gridRowsInput.addEventListener('input', () => {
        const val = parseInt(gridRowsInput.value);
        rowsValue.textContent = val;
        markSettingsChanged();
        shader.setGridSize(parseInt(gridColsInput.value), val);
        scheduleUpdateUI();
    });
    
    gapSlider.addEventListener('input', () => {
        const val = parseInt(gapSlider.value);
        gapValue.textContent = val;
        markSettingsChanged();
        shader.setGap(val / 100);
        scheduleUpdateUI();
    });

    if (cornerRadiusSlider) {
        cornerRadiusSlider.addEventListener('input', () => {
            const val = parseInt(cornerRadiusSlider.value);
            if (cornerRadiusValue) cornerRadiusValue.textContent = val + '%';
            markSettingsChanged();
            shader.setCornerRadius?.(val / 100);
            scheduleUpdateUI();
        });
    }

    if (sourceLightenSlider) {
        sourceLightenSlider.addEventListener('input', () => {
            const val = parseInt(sourceLightenSlider.value || '0', 10) || 0;
            if (sourceLightenValue) {
                const prefix = val > 0 ? '+' : '';
                sourceLightenValue.textContent = `${prefix}${val}%`;
            }
            markSettingsChanged();
            shader.setSourceLighten?.(val / 100);
            scheduleUpdateUI();
        });
    }

    if (sourceBwToggle) {
        sourceBwToggle.addEventListener('change', (e) => {
            const enabled = e.target.checked;
            markSettingsChanged();
            shader.setSourceGrayscale?.(enabled);
            scheduleUpdateUI();
        });
    }
    
    colorCountSlider.addEventListener('input', () => {
        const val = parseInt(colorCountSlider.value);
        colorCountValue.textContent = val;
        markSettingsChanged();
        shader.setTargetColors(val);
        scheduleUpdateUI();
    });

    if (extractionModeSelect) {
        extractionModeSelect.addEventListener('change', (e) => {
            const value = e.target.value;
            markSettingsChanged();
            shader.setExtractionMode?.(value);
            syncExtractionDependentControls(value);
            scheduleUpdateUI({ immediate: true });
        });

        // Initial state
        syncExtractionDependentControls(extractionModeSelect.value);
    }

    thresholdSlider.addEventListener('input', () => {
        const val = parseInt(thresholdSlider.value);
        thresholdValue.textContent = val + '%';
        markSettingsChanged();
        shader.setGroupThreshold(val);
        syncGroupRectDependentControls();
        scheduleUpdateUI();
    });
    
    revealSlider.addEventListener('input', () => {
        const val = parseInt(revealSlider.value);
        revealValue.textContent = val + '%';
        markSettingsChanged();
        shader.setRevealSize(val);
        syncRevealDependentControls();
        scheduleUpdateUI();
    });

    if (revealDetailsToggle) {
        revealDetailsToggle.addEventListener('change', (e) => {
            const enabled = e.target.checked;
            markSettingsChanged();
            shader.setRevealDetails?.(enabled);
            scheduleUpdateUI();
        });
    }

    if (invertRevealToggle) {
        invertRevealToggle.addEventListener('change', (e) => {
            const enabled = e.target.checked;
            markSettingsChanged();
            shader.setInvertReveal?.(enabled);
            scheduleUpdateUI();
        });
    }

    if (effectSelect) {
        effectSelect.addEventListener('change', (e) => {
            const value = e.target.value;
            markSettingsChanged();
            applyEffectToShader(value);
        });
    }

    if (hideSourceToggle) {
        hideSourceToggle.addEventListener('change', (e) => {
            const enabled = e.target.checked;
            markSettingsChanged();
            shader.setHideSource?.(enabled);
            scheduleUpdateUI();
        });
    }

    if (groupRectModeToggle) {
        groupRectModeToggle.addEventListener('change', (e) => {
            const enabled = e.target.checked;
            markSettingsChanged();
            shader.setGroupRectMode?.(enabled);
            scheduleUpdateUI();
        });
    }
    
    webcamBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        try {
            webcamBtn.textContent = '⏳ Starting...';
            await shader.loadWebcam();
            dropZone.classList.add('has-image');
            shader.onFrameUpdate = onShaderFrame;
            webcamBtn.textContent = '📷 Webcam';
            setMediaActive({ webcam: true, video: true });
            sourceLoaded = true;
            updateActionButtons();
            await updateSwitchCameraAvailability();
            scheduleUpdateUI({ immediate: true });
        } catch (err) {
            webcamBtn.textContent = '📷 Webcam';
            setMediaActive({ webcam: false, video: false });
            alert('Could not access webcam: ' + err.message);
        }
    });

    // Initial state
    updateActionButtons();

    const isIOS = () => {
        // iPadOS reports as Mac, but has touch points.
        const ua = navigator.userAgent || '';
        const isAppleMobile = /iPad|iPhone|iPod/.test(ua);
        const isIPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
        return isAppleMobile || isIPadOS;
    };

    const flashCanvas = () => {
        if (!canvas) return;
        canvas.classList.remove('is-flashing');
        // Restart animation reliably
        void canvas.offsetWidth;
        canvas.classList.add('is-flashing');
        window.setTimeout(() => canvas.classList.remove('is-flashing'), 240);
    };

    const savePNGDataUrl = async (dataUrl, filename) => {
        // iOS Safari cannot programmatically save to Photos.
        // The closest "save like an image from the web" UX is to open the image (new tab),
        // so the user can use the native Share / long-press "Save Image".
        if (isIOS()) {
            try {
                const a = document.createElement('a');
                a.href = dataUrl;
                a.target = '_blank';
                a.rel = 'noopener,noreferrer';
                a.click();
                return;
            } catch {
                // Fall back to Share Sheet below.
            }
        }

        // Prefer Share Sheet (iOS: lets user pick "Save Image" into Photos).
        try {
            const blob = await (await fetch(dataUrl)).blob();
            const file = new File([blob], filename, { type: blob.type || 'image/png' });

            const canShareFiles = typeof navigator !== 'undefined'
                && typeof navigator.share === 'function'
                && (typeof navigator.canShare !== 'function' || navigator.canShare({ files: [file] }));

            if (canShareFiles) {
                try {
                    await navigator.share({ files: [file], title: filename });
                    return;
                } catch (err) {
                    // If user cancels, don't fall back to downloads.
                    if (err?.name === 'AbortError') return;
                }
            }

            // Download fallback (works in most non-iOS browsers).
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            // Last resort: open the image so iOS users can long-press "Save Image".
            if (isIOS()) {
                window.open(dataUrl, '_blank', 'noopener,noreferrer');
                return;
            }
            const a = document.createElement('a');
            a.href = dataUrl;
            a.download = filename;
            a.click();
        }
    };

    if (switchCameraBtn) {
        switchCameraBtn.addEventListener('click', async (e) => {
            e.preventDefault();
            e.stopPropagation();
            switchCameraBtn.disabled = true;
            try {
                await shader.switchWebcamCamera();
                shader.onFrameUpdate = onShaderFrame;
                await updateSwitchCameraAvailability();
                scheduleUpdateUI({ immediate: true });
            } catch (err) {
                alert('Could not switch camera: ' + err.message);
            } finally {
                switchCameraBtn.disabled = false;
            }
        });
    }

    if (pauseBtn) {
        pauseBtn.addEventListener('click', async (e) => {
            e.preventDefault();
            e.stopPropagation();
            try {
                const paused = shader.toggleVideoPause?.();
                pauseBtn.textContent = paused ? 'Resume' : 'Pause';
                pauseBtn.dataset.paused = paused ? 'true' : 'false';
            } catch (err) {
                alert('Could not pause: ' + err.message);
            }
        });
    }
    
    function getExportJSON() {
        const data = shader.getGridData();
        return JSON.stringify({
            canvas: {
                width: canvas.width,
                height: canvas.height
            },
            grid: {
                columns: shader.gridCols,
                rows: shader.gridRows
            },
            cells: data.map(cell => ({
                position: { row: cell.row, col: cell.col },
                bounds: { x: cell.x, y: cell.y, width: cell.width, height: cell.height },
                color: cell.color
            }))
        }, null, 2);
    }
    
    copyJsonBtn.addEventListener('click', () => {
        const json = getExportJSON();
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'color-grid.json';
        a.click();
        URL.revokeObjectURL(url);
    });
    
    downloadPngBtn.addEventListener('click', () => {
        const dataUrl = shader.exportPNG();
        savePNGDataUrl(dataUrl, 'color-grid.png');
    });

    if (triggerCaptureBtn) {
        triggerCaptureBtn.addEventListener('click', async (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!videoActive || !sourceLoaded) return;

            triggerCaptureBtn.disabled = true;
            try {
                flashCanvas();
                const dataUrl = shader.exportPNG();
                await savePNGDataUrl(dataUrl, 'color-grid.png');
            } finally {
                updateActionButtons();
            }
        });
    }
});
